import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import type { Card } from '@shared/types/game';
import { MAX_LIVE_PLAYERS } from '@shared/types/live';
import {
  SUIT_SYMBOLS,
  isBlindRound,
  isRedSuit,
  legalBids,
  legalPlays,
  sortHand,
} from '@shared/game-logic';
import { Button, Panel, Screen } from '@/components/ui';
import { CardBack, PlayingCard, cardName } from '@/components/PlayingCard';
import BoerenbridgeTable from '@/components/BoerenbridgeTable';
import FannedHand from '@/components/FannedHand';
import { describeSettings } from '@/components/GameSettings';
import { CountUp } from '@/components/CountUp';
import Notices from '@/components/Notices';
import { useLiveSession } from '@/live/useLiveSession';
import { useFeedbackBridge } from '@/live/useFeedbackBridge';
import { playCue } from '@/lib/feedback';
import { isMyTurn, myHand, myPlayer, useLive } from '@/store/live';
import { SUIT_LABELS, seatColor, signed, trumpLabel } from '@/lib/labels';

export default function LivePlay() {
  const { code } = useParams<{ code: string }>();
  const location = useLocation() as { state?: { name?: string; profileId?: string | null } };
  const actions = useLiveSession(
    code,
    location.state?.name ?? null,
    location.state?.profileId ?? null
  );
  useFeedbackBridge();

  const data = useLive();
  const { state, status, error, notices, standings } = data;
  const me = myPlayer(data);

  if (!state) {
    return (
      <Screen title={`Potje ${code ?? ''}`} back="/">
        <Panel title="Verbinden">
          <p className="text-sm text-slate-400">
            {status === 'reconnecting'
              ? 'De verbinding is weg. Er wordt opnieuw geprobeerd…'
              : 'Bezig met verbinden met het potje…'}
          </p>
          {error && <p className="mt-2 text-sm text-bad">{error}</p>}
        </Panel>
      </Screen>
    );
  }

  return (
    <Screen
      title={state.phase === 'lobby' ? `Code ${state.code}` : `Ronde ${state.roundNo}`}
      back="/"
      subtitle={
        state.phase === 'lobby'
          ? `${state.players.length} ${state.players.length === 1 ? 'speler' : 'spelers'}`
          : `${state.cards} ${state.cards === 1 ? 'kaart' : 'kaarten'} · troef ${trumpLabel(state.trumpSuit)}`
      }
      actions={<ConnectionDot status={status} />}
    >
      {error && (
        <button
          type="button"
          role="alert"
          onClick={() => useLive.getState().dismissError()}
          className="w-full rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2 text-left text-sm text-red-200"
        >
          {error} <span className="text-red-300/60">— tik om te sluiten</span>
        </button>
      )}

      {state.phase === 'lobby' && <Lobby actions={actions} />}
      {(state.phase === 'bidding' || state.phase === 'playing') && <Table actions={actions} />}
      {state.phase === 'scoring' && (
        <>
          {/* The table stays up through scoring: otherwise the last trick of a
              round disappears the moment the round ends, before anyone has seen
              who took it. */}
          <BoerenbridgeTable state={state} mySeat={data.seat} blind={false} />
          <RoundResult />
          <LiveStandings />
        </>
      )}
      {state.phase === 'finished' && <Finished standings={standings} />}

      <Notices items={notices} limit={2} show={state.phase !== 'lobby'} />

      {me && state.phase !== 'lobby' && (
        <p className="text-center text-xs text-slate-600">
          Je speelt als {me.name} · totaal {signed(me.total)}
        </p>
      )}
    </Screen>
  );
}

function ConnectionDot({ status }: { status: string }) {
  const label =
    status === 'open' ? 'verbonden' : status === 'reconnecting' ? 'opnieuw verbinden' : status;
  const color =
    status === 'open' ? 'bg-good' : status === 'closed' ? 'bg-slate-500' : 'bg-amber-400';
  return (
    <span className="flex items-center gap-1.5 text-xs text-slate-400" title={label}>
      <span className={`h-2 w-2 rounded-full ${color}`} aria-hidden />
      <span className="sr-only">{label}</span>
    </span>
  );
}

// --- lobby -----------------------------------------------------------------

function Lobby({ actions }: { actions: ReturnType<typeof useLiveSession> }) {
  const data = useLive();
  const state = data.state!;
  const me = myPlayer(data);
  const everyoneReady = state.players.every((player) => player.isReady);
  const enough = state.players.length >= 3;
  const bots = state.players.filter((player) => player.isBot).length;
  const tableFull = state.players.length >= MAX_LIVE_PLAYERS;

  const joinUrl = `${window.location.origin}/join/${state.code}`;

  return (
    <>
      <Panel title="Deel deze code">
        <p className="text-center font-tabular text-5xl font-bold tracking-[0.2em]">{state.code}</p>
        <p className="mt-3 break-all text-center text-sm text-slate-400">{joinUrl}</p>
        <div className="mt-3">
          <Button
            full
            onClick={() => {
              void navigator.clipboard?.writeText(joinUrl);
            }}
          >
            Link kopiëren
          </Button>
        </div>
      </Panel>

      {/*
        The rules, for everyone.
        This was the gap: three children tapped "klaar" without knowing whether
        the descent is played without trump or how a round is counted. The host
        chose it and then could not see it either.
      */}
      <Panel title="Regels van dit potje">
        <p className="text-sm text-slate-300">
          {describeSettings({
            maxCards: state.config.roundPlan.maxCards,
            direction: state.config.roundPlan.direction,
            extraNoTrumpRound: state.config.roundPlan.extraNoTrumpRound,
            noTrumpOnTheWayDown: state.config.roundPlan.noTrumpOnTheWayDown,
            repeatTopRound: state.config.roundPlan.repeatTopRound,
            scoringPreset: state.config.scoringPreset,
            houseRules: state.config.houseRules,
          })}
        </p>
        <p className="mt-2 text-xs text-slate-500">
          Vastgelegd toen het potje werd aangemaakt; halverwege wijzigen kan niet.
        </p>
      </Panel>

      <Panel title="Aan tafel">
        <ul className="space-y-2">
          {state.players.map((player) => (
            <li key={player.seat} className="flex items-center gap-3">
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: seatColor(player.seat) }}
              />
              <span className="min-w-0 flex-1 truncate">
                {player.name}
                {player.isHost && <span className="ml-2 text-xs text-slate-500">host</span>}
                {/* A computer is never "geen verbinding": it has no connection to
                    lose, and saying so would send you looking for a phone. */}
                {player.isBot && <span className="ml-2 text-xs text-slate-500">computer</span>}
                {!player.isBot && !player.isConnected && (
                  <span className="ml-2 text-xs text-amber-400">geen verbinding</span>
                )}
              </span>
              <span className={player.isReady ? 'text-sm text-good' : 'text-sm text-slate-500'}>
                {player.isReady ? 'klaar' : 'wacht'}
              </span>
            </li>
          ))}
        </ul>
        {!enough && (
          <p className="mt-3 text-sm text-slate-500">
            Boerenbridge speelt vanaf drie spelers. Wacht tot er genoeg mensen zijn, of zet er
            computerspelers bij.
          </p>
        )}
      </Panel>

      {me?.isHost && (
        <div className="flex gap-2">
          <Button full onClick={actions.addBot} disabled={tableFull}>
            Computerspeler erbij
          </Button>
          {bots > 0 && (
            <Button full variant="secondary" onClick={actions.removeBot}>
              Laatste weg
            </Button>
          )}
        </div>
      )}

      {me && (
        <div className="space-y-2">
          <Button
            full
            variant={me.isReady ? 'secondary' : 'primary'}
            onClick={() => actions.ready(!me.isReady)}
          >
            {me.isReady ? 'Toch nog even niet' : 'Ik ben klaar'}
          </Button>
          {me.isHost && (
            <Button
              full
              variant="primary"
              disabled={!enough || !everyoneReady}
              onClick={actions.start}
            >
              {!enough
                ? 'Nog niet genoeg spelers'
                : !everyoneReady
                  ? 'Niet iedereen staat op klaar'
                  : 'Start het potje'}
            </Button>
          )}
        </div>
      )}
    </>
  );
}

// --- the table -------------------------------------------------------------

function Table({ actions }: { actions: ReturnType<typeof useLiveSession> }) {
  const data = useLive();
  const state = data.state!;
  const mine = isMyTurn(data);
  const blind = isBlindRound({
    cards: state.cards,
    phase: state.phase,
    houseRules: state.config.houseRules,
  });

  return (
    <>
      <BoerenbridgeTable state={state} mySeat={data.seat} blind={blind} />

      {state.phase === 'bidding' ? (
        <BidPanel actions={actions} blind={blind} />
      ) : (
        <HandPanel actions={actions} playable={mine} blind={blind} />
      )}

      <LiveStandings />
    </>
  );
}

function BidPanel({
  actions,
  blind,
}: {
  actions: ReturnType<typeof useLiveSession>;
  blind: boolean;
}) {
  const data = useLive();
  const state = data.state!;
  const me = myPlayer(data);
  const mine = isMyTurn(data);

  if (!me) return null;

  // Mirror the server's rule so an illegal bid is never even offered. The
  // server checks again — this is about explaining, not about trust.
  const order = Array.from(
    { length: state.players.length },
    (_, i) => (state.dealerSeat + 1 + i) % state.players.length
  );
  const myIndex = order.indexOf(me.seat);
  const isLastBidder = myIndex === order.length - 1;
  const bidsSoFar = order
    .slice(0, Math.max(myIndex, 0))
    .map((seat) => state.players[seat].bid)
    .filter((bid): bid is number => bid !== null);

  const allowed = legalBids(state.cards, bidsSoFar, isLastBidder, state.config.houseRules);
  const forbidden = Array.from({ length: state.cards + 1 }, (_, i) => i).filter(
    (bid) => !allowed.includes(bid)
  );

  if (me.bid !== null) {
    return (
      <Panel title="Jouw bod">
        <p className="text-2xl font-semibold tabular-nums">{me.bid}</p>
        <p className="mt-1 text-sm text-slate-400">
          {mine ? '' : `Wachten op ${state.players[state.turnSeat ?? 0]?.name ?? 'de rest'}…`}
        </p>
      </Panel>
    );
  }

  return (
    <Panel title={mine ? 'Hoeveel slagen haal jij?' : 'Bieden'}>
      {!mine ? (
        <p className="text-sm text-slate-400">
          {state.players[state.turnSeat ?? 0]?.name ?? 'Iemand'} is aan de beurt om te bieden.
        </p>
      ) : (
        <>
          <div className="flex flex-wrap gap-2">
            {Array.from({ length: state.cards + 1 }, (_, bid) => bid).map((bid) => {
              const blocked = forbidden.includes(bid);
              return (
                <button
                  key={bid}
                  type="button"
                  disabled={blocked}
                  onClick={() => actions.bid(bid)}
                  aria-label={`Bied ${bid}`}
                  className={[
                    'h-12 w-12 rounded-xl text-lg font-semibold tabular-nums',
                    blocked
                      ? 'cursor-not-allowed bg-base-800/50 text-slate-600 line-through'
                      : 'bg-base-700 text-white hover:bg-base-600',
                  ].join(' ')}
                >
                  {bid}
                </button>
              );
            })}
          </div>
          {isLastBidder && forbidden.length > 0 && (
            <p className="mt-3 text-sm text-slate-400">
              Jij deelt en biedt als laatste, dus je mag {forbidden.join(' of ')} niet zeggen: dan
              zou het totaal precies {state.cards} zijn en zou iedereen zijn bod kunnen halen.
            </p>
          )}
        </>
      )}
      {blind ? <BlindNotice /> : <HandPreview />}
    </Panel>
  );
}

/**
 * The blind round explained where it happens. Kids meet this rule for the first
 * time and it looks like a bug until somebody says out loud what is going on.
 */
function BlindNotice() {
  return (
    <div className="mt-4 rounded-xl border-l-2 border-accent-500 bg-accent-500/10 px-3 py-2 text-sm text-slate-200">
      <p className="font-medium">Blinde ronde</p>
      <p className="mt-1 text-slate-300">
        Je houdt je enige kaart naar buiten. Je ziet dus de kaart van iedere ander — hierboven op
        tafel — maar niet je eigen. Bied op wat je bij de anderen ziet liggen.
      </p>
    </div>
  );
}

/** Your cards while bidding — you need to see them to decide, but not play them. */
function HandPreview() {
  const data = useLive();
  const state = data.state!;
  const hand = sortHand(myHand(data), state.trumpSuit);

  return (
    <div className="mt-4">
      <p className="mb-2 text-sm text-slate-400">Jouw kaarten</p>
      {hand.length === 0 ? (
        <CardBack />
      ) : (
        <FannedHand cardWidth={56}>
          {hand.map((card) => (
            <PlayingCard key={`${card.suit}-${card.rank}`} card={card} size="md" />
          ))}
        </FannedHand>
      )}
    </div>
  );
}

function HandPanel({
  actions,
  playable,
  blind,
}: {
  actions: ReturnType<typeof useLiveSession>;
  playable: boolean;
  blind: boolean;
}) {
  const data = useLive();
  const state = data.state!;
  const hand = myHand(data);
  const sorted = sortHand(hand, state.trumpSuit);
  const [refused, setRefused] = useState<string | null>(null);

  // A new trick clears the explanation; it no longer applies.
  const trickLength = state.currentTrick.length;
  useEffect(() => setRefused(null), [trickLength, state.roundNo]);

  const legal = legalPlays(hand, state.currentTrick, state.trumpSuit, state.config.houseRules);
  const isLegal = (card: Card) =>
    legal.some((option) => option.suit === card.suit && option.rank === card.rank);

  const waitingOn = state.turnSeat !== null ? state.players[state.turnSeat]?.name : null;

  // In a blind round the client genuinely does not know which card it holds, so
  // there is nothing to pick: it asks the server to play the only one.
  if (blind) {
    return (
      <Panel title={playable ? 'Jij bent aan zet' : 'Jouw kaart'}>
        <div className="flex items-center gap-4">
          <CardBack size="md" />
          <div className="min-w-0 flex-1">
            <p className="text-sm text-slate-300">
              Je eigen kaart blijft verborgen. Wat het is zie je zodra hij op tafel ligt.
            </p>
            {!playable && waitingOn && (
              <p className="mt-1 text-sm text-slate-500">Wachten op {waitingOn}…</p>
            )}
          </div>
        </div>
        {playable && (
          <div className="mt-4">
            <Button variant="primary" full onClick={actions.playBlind}>
              Speel je kaart
            </Button>
          </div>
        )}
      </Panel>
    );
  }

  const ledSuit = state.currentTrick[0]?.card.suit;

  return (
    <Panel title={playable ? 'Jij bent aan zet' : 'Jouw kaarten'}>
      {!playable && waitingOn && (
        <p className="mb-3 text-sm text-slate-400">Wachten op {waitingOn}…</p>
      )}
      {/* An overlapping arc, the way you hold cards — see FannedHand for why the
          rotation must live on a wrapper and not on the card itself. */}
      <FannedHand cardWidth={56} className="py-2">
        {sorted.map((card, index) => {
          const allowed = isLegal(card);
          return (
            <PlayingCard
              key={`${card.suit}-${card.rank}`}
              card={card}
              size="md"
              dimmed={playable && !allowed}
              // An illegal card stays tappable on purpose. A dead button just
              // looks broken to a child; tapping it should say why not.
              disabled={!playable}
              onClick={() => {
                if (allowed) {
                  setRefused(null);
                  actions.play(card);
                } else {
                  // No server message for this — the client refuses locally, so
                  // the cue is played straight from here.
                  setRefused(cardName(card));
                  playCue({ kind: 'refused' });
                }
              }}
              label={`Speel ${cardName(card)}${playable && !allowed ? ' (mag nu niet)' : ''}`}
              className="animate-deal-in"
              // Staggered, so a fresh hand fans in instead of appearing at once.
              style={{ animationDelay: `${Math.min(index, 12) * 45}ms` }}
            />
          );
        })}
      </FannedHand>

      {refused && ledSuit && (
        <p
          role="alert"
          className="mt-3 animate-pop-in rounded-xl border-l-2 border-amber-400 bg-amber-400/10 px-3 py-2 text-sm text-amber-100"
        >
          {refused} mag nu niet: er is{' '}
          <span className={isRedSuit(ledSuit) ? 'text-red-400' : ''}>
            {SUIT_SYMBOLS[ledSuit]} {SUIT_LABELS[ledSuit]}
          </span>{' '}
          gevraagd en die kleur heb je nog. Volgen is verplicht.
        </p>
      )}
      {playable && !refused && legal.length < hand.length && (
        <p className="mt-3 text-sm text-slate-400">
          Je moet {SUIT_SYMBOLS[state.currentTrick[0]?.card.suit ?? 'spades']}{' '}
          <span
            className={
              isRedSuit(state.currentTrick[0]?.card.suit ?? 'spades') ? 'text-red-400' : ''
            }
          >
            volgen
          </span>{' '}
          — de andere kaarten kun je nu niet spelen.
        </p>
      )}
    </Panel>
  );
}

// --- between rounds --------------------------------------------------------

/**
 * The score of everyone at the table, plus the round-by-round history.
 *
 * The seat badges carry the running total already, but "how did we get here" is
 * a question that comes up every round, and there is no scorecard to point at in
 * a live game.
 */
function LiveStandings() {
  const data = useLive();
  const state = data.state!;

  const rows = [...state.players].sort((a, b) => b.total - a.total || a.seat - b.seat);
  const scored = state.rounds.length;

  return (
    <Panel
      title={scored === 0 ? 'Stand' : `Stand na ${scored} ${scored === 1 ? 'ronde' : 'rondes'}`}
    >
      <ol className="space-y-1.5">
        {rows.map((player, index) => (
          <li key={player.seat} className="flex items-center gap-3">
            <span className="w-4 text-sm tabular-nums text-slate-500">{index + 1}.</span>
            <span
              className="h-2.5 w-2.5 shrink-0 rounded-full"
              style={{ backgroundColor: seatColor(player.seat) }}
            />
            <span className="min-w-0 flex-1 truncate">
              {player.name}
              {player.seat === data.seat && <span className="ml-1 text-slate-500">(jij)</span>}
            </span>
            {player.bid !== null && state.phase !== 'lobby' && (
              <span className="text-xs tabular-nums text-slate-500">
                nu {player.tricksWon}/{player.bid}
              </span>
            )}
            <CountUp
              value={player.total}
              signed
              className="w-10 text-right font-semibold tabular-nums"
            />
          </li>
        ))}
      </ol>

      {scored > 0 && (
        <details className="mt-3">
          <summary className="cursor-pointer text-sm text-slate-400">Per ronde bekijken</summary>
          <div className="mt-2 -mx-1 overflow-x-auto px-1">
            <table className="w-full min-w-max border-collapse text-sm">
              <thead>
                <tr>
                  <th className="px-1.5 py-1 text-left text-xs font-medium text-slate-500">
                    Ronde
                  </th>
                  {state.players.map((player) => (
                    <th key={player.seat} className="px-1.5 py-1 text-right text-xs font-medium">
                      <span className="flex items-center justify-end gap-1">
                        <span
                          className="h-1.5 w-1.5 rounded-full"
                          style={{ backgroundColor: seatColor(player.seat) }}
                        />
                        <span className="max-w-14 truncate">{player.name}</span>
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {state.rounds.map((round) => (
                  <tr key={round.roundNo} className="border-t border-white/5">
                    <th scope="row" className="px-1.5 py-1 text-left font-normal tabular-nums">
                      {round.roundNo}
                      <span className="ml-1 text-xs text-slate-500">
                        {round.cards}k{round.trump === null ? 'Ø' : ''}
                      </span>
                    </th>
                    {state.players.map((player) => {
                      const entry = round.entries.find((e) => e.seat === player.seat);
                      return (
                        <td
                          key={player.seat}
                          className="px-1.5 py-1 text-right tabular-nums"
                          title={`bod ${entry?.bid ?? '–'}, gehaald ${entry?.tricksWon ?? '–'}`}
                        >
                          <span className="text-xs text-slate-500">
                            {entry?.bid ?? '–'}/{entry?.tricksWon ?? '–'}
                          </span>{' '}
                          <span
                            className={
                              (entry?.points ?? 0) >= 0
                                ? 'font-medium text-good'
                                : 'font-medium text-bad'
                            }
                          >
                            {signed(entry?.points ?? 0)}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-1 text-xs text-slate-500">Per vakje: bod/gehaald en de punten.</p>
        </details>
      )}
    </Panel>
  );
}

function RoundResult() {
  const data = useLive();
  const state = data.state!;
  const round = state.rounds[state.rounds.length - 1];

  if (!round) return null;

  return (
    <Panel title={`Ronde ${round.roundNo} geteld`}>
      <ul className="space-y-2">
        {round.entries.map((entry) => {
          const player = state.players[entry.seat];
          const exact = entry.bid === entry.tricksWon;
          return (
            <li
              key={entry.seat}
              className={[
                'flex items-center gap-3 rounded-lg px-1',
                exact ? 'animate-flash-good' : 'animate-flash-bad',
              ].join(' ')}
            >
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: seatColor(entry.seat) }}
              />
              <span className="min-w-0 flex-1 truncate">{player?.name}</span>
              <span className="text-sm text-slate-400">
                bood {entry.bid}, haalde {entry.tricksWon}
              </span>
              <span
                className={[
                  'w-12 text-right font-semibold tabular-nums',
                  exact ? 'text-good' : 'text-bad',
                ].join(' ')}
              >
                {signed(entry.points ?? 0)}
              </span>
            </li>
          );
        })}
      </ul>
      <p className="mt-3 text-sm text-slate-500">De volgende ronde wordt zo gedeeld…</p>
    </Panel>
  );
}

function Finished({
  standings,
}: {
  standings: Array<{ seat: number; name: string; total: number; placement: number }> | null;
}) {
  const data = useLive();
  const state = data.state!;
  const rows =
    standings ??
    [...state.players]
      .sort((a, b) => b.total - a.total)
      .map((player, index) => ({
        seat: player.seat,
        name: player.name,
        total: player.total,
        placement: index + 1,
      }));

  const medals = ['🥇', '🥈', '🥉'];
  /*
   * A table with a computer player does not go into the standings — the server
   * skips writing it. Saying otherwise here would be the app telling you
   * something untrue about its own database, which is worse than saying nothing.
   */
  const practice = state.players.some((player) => player.isBot);

  return (
    <Panel title="Eindstand">
      <ol className="space-y-2">
        {rows.map((row, index) => (
          <li
            key={row.seat}
            className="animate-deal-in flex items-center gap-3 rounded-xl bg-base-900/50 px-3 py-2.5"
            style={{ animationDelay: `${index * 90}ms` }}
          >
            <span className="w-6 text-center text-lg">
              {medals[row.placement - 1] ?? (
                <span className="text-sm text-slate-500">{row.placement}</span>
              )}
            </span>
            <span
              className="h-2.5 w-2.5 shrink-0 rounded-full"
              style={{ backgroundColor: seatColor(row.seat) }}
            />
            <span className="min-w-0 flex-1 truncate font-medium">{row.name}</span>
            <CountUp value={row.total} className="text-lg font-bold tabular-nums" />
          </li>
        ))}
      </ol>
      <p className="mt-3 text-sm text-slate-500">
        {practice
          ? 'Een potje met een computerspeler telt niet mee in de ranglijst.'
          : 'Dit potje telt mee in de ranglijst onder Historie.'}
      </p>
    </Panel>
  );
}
